'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';

export default function NavigatingRateCyclesPage() {
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}
    >
      {/* Header */}
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-12 lg:px-20"
        style={{
          height: '72px',
          backgroundColor: 'rgba(13,26,58,0.95)',
          borderBottom: '1px solid rgba(30,48,104,0.6)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Link
          href="/#insights"
          className="flex items-center gap-2 text-sm font-medium transition-colors"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <ArrowLeft size={15} />
          Back to Perspectives
        </Link>
        <div className="flex items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/assets/macro-capital-logo.svg" alt="Macro Capital" style={{ width: '36px', height: '36px' }} />
          <span className="text-sm font-bold tracking-widest uppercase" style={{ color: 'var(--foreground)' }}>
            Macro Capital
          </span>
        </div>
      </header>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 py-16">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <span
            className="text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full"
            style={{
              backgroundColor: 'rgba(200,129,58,0.15)',
              border: '1px solid rgba(200,129,58,0.3)',
              color: 'var(--primary)',
            }}
          >
            Featured · Macro
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Calendar size={11} /> April 2025
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Clock size={11} /> 8 min read
          </span>
        </div>

        {/* Title */}
        <h1
          className="font-semibold leading-tight mb-6"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            color: 'var(--foreground)',
          }}
        >
          Navigating Rate Cycles: What History Tells Us
        </h1>

        <p className="text-lg leading-relaxed mb-10" style={{ color: 'var(--muted-foreground)' }}>
          Central bank policy pivots have historically created asymmetric opportunities for patient investors. We examine the patterns across five decades of monetary tightening and easing cycles — and what they mean for capital allocation today.
        </p>

        <div className="h-px mb-10" style={{ backgroundColor: 'var(--border)' }} />

        {/* Body */}
        <div className="prose-article space-y-8" style={{ color: 'var(--muted-foreground)', lineHeight: '1.85' }}>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              The Architecture of a Rate Cycle
            </h2>
            <p className="mb-4">
              Monetary policy does not move in straight lines. It oscillates — sometimes violently — between the competing imperatives of price stability and economic growth. Since the early 1970s, when the Bretton Woods system collapsed and central banks gained genuine independence over interest rate policy, we have witnessed at least seven distinct tightening cycles in developed markets, each followed by an easing phase of varying depth and duration.
            </p>
            <p className="mb-4">
              The architecture of each cycle shares common features: an initial phase of denial, where markets price in only modest tightening; a recognition phase, where the full extent of the hiking path becomes apparent; a peak phase, often accompanied by credit stress or recessionary signals; and finally, a pivot phase, where the central bank reverses course. It is in the pivot phase — and the months immediately preceding it — that the most durable investment opportunities tend to emerge.
            </p>
            <p>
              Understanding where we sit within this architecture is not merely an academic exercise. It is the foundation of sound capital allocation. Investors who misread the cycle — who treat a temporary pause as a pivot, or who underestimate the duration of a tightening phase — consistently underperform those who maintain a clear-eyed view of the monetary backdrop.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Five Decades of Evidence
            </h2>
            <p className="mb-4">
              The Volcker era of the early 1980s remains the most instructive case study. Faced with inflation running above 13%, Federal Reserve Chairman Paul Volcker engineered the most aggressive tightening cycle in modern history, pushing the federal funds rate to 20% by June 1981. The short-term pain was severe — the US economy entered two recessions in quick succession, unemployment peaked at nearly 11%, and equity markets fell sharply.
            </p>
            <p className="mb-4">
              Yet the investors who maintained conviction through this period — who understood that the tightening was necessary and that the subsequent easing would be powerful — were rewarded handsomely. The 1982–2000 bull market in equities and bonds was, in large part, the dividend paid to those who held through the Volcker shock.
            </p>
            <p className="mb-4">
              The 1994 tightening cycle offers a different lesson. The Fed raised rates by 300 basis points in twelve months, triggering a bond market rout that became known as the "Great Bond Massacre." Yet equities, after an initial correction, resumed their upward trajectory within months. The cycle was short, the economy remained healthy, and the pivot came quickly. Investors who panicked and de-risked in early 1994 missed one of the strongest equity rallies of the decade.
            </p>
            <p>
              The 2004–2006 cycle was characterised by its gradualism — the Fed raised rates in 25 basis point increments at seventeen consecutive meetings, telegraphing its intentions clearly. This predictability suppressed volatility and allowed risk assets to perform well throughout the tightening phase. The lesson: the pace and predictability of tightening matters as much as its magnitude.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              The 2022–2024 Cycle in Context
            </h2>
            <p className="mb-4">
              The most recent tightening cycle — which began in March 2022 and saw the Fed raise rates by 525 basis points in under two years — was remarkable for its speed. It was the fastest tightening cycle since Volcker, yet it occurred against a backdrop of historically low starting rates, meaning the absolute level of rates, even at the peak, remained below the averages of the 1980s and 1990s.
            </p>
            <p className="mb-4">
              The consequences were predictable in retrospect, if not in real time. Duration-sensitive assets — long-dated government bonds, growth equities, real estate — suffered significant drawdowns. The Bloomberg US Aggregate Bond Index fell more than 15% in 2022, its worst calendar year performance in modern history. Meanwhile, short-duration assets, floating-rate instruments, and commodities outperformed.
            </p>
            <p className="mb-4">
              What was less predictable was the resilience of the labour market and consumer spending. The "soft landing" that many economists dismissed as impossible in 2022 was, by most measures, achieved. This resilience extended the tightening cycle and delayed the pivot — a reminder that economic forecasting, even by the most sophisticated practitioners, is an exercise in humility.
            </p>
            <p>
              As we move through 2025 and into 2026, the question is no longer whether the Fed will cut rates — it has — but rather how deep and how durable the easing cycle will be. The answer depends critically on the trajectory of inflation, which remains above target in several categories, and on the labour market, which has shown surprising stickiness.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Asymmetric Opportunities in the Pivot Phase
            </h2>
            <p className="mb-4">
              History is consistent on one point: the pivot phase of a rate cycle is among the most fertile periods for active investors. The reason is structural. As rates peak and begin to fall, the discount rate applied to future cash flows declines, mechanically increasing the present value of long-duration assets. Simultaneously, the cost of capital for businesses falls, improving margins and investment economics. And the psychological shift from fear to relief creates a powerful tailwind for risk assets.
            </p>
            <p className="mb-4">
              The data bears this out. In the twelve months following the first Fed rate cut in each of the last six easing cycles, the S&P 500 has delivered an average return of approximately 18%. Investment-grade credit spreads have tightened by an average of 60–80 basis points. Emerging market equities, which are particularly sensitive to the dollar and to global risk appetite, have outperformed developed markets in four of the six cycles.
            </p>
            <p className="mb-4">
              But the opportunity is not uniform. Within the pivot phase, the quality of the underlying business matters enormously. In the early stages of easing, when uncertainty remains high, the market tends to reward quality — companies with strong balance sheets, durable competitive advantages, and predictable cash flows. It is only in the later stages, when confidence is restored, that lower-quality cyclicals and speculative assets tend to outperform.
            </p>
            <p>
              This sequencing has important implications for portfolio construction. A strategy that maintains a quality bias through the tightening phase, then selectively adds cyclical exposure as the easing cycle matures, has historically outperformed both a static allocation and a purely momentum-driven approach.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              What This Means for Capital Allocation Today
            </h2>
            <p className="mb-4">
              As of Q2 2026, we believe we are in the early-to-middle stages of the current easing cycle. The Fed has cut rates, but the pace of further cuts remains uncertain. Inflation has moderated but not fully normalised. The labour market is cooling but not collapsing. This is a classic "soft landing" environment — constructive for risk assets, but not without pockets of vulnerability.
            </p>
            <p className="mb-4">
              In this environment, our portfolio positioning reflects several convictions. First, we maintain a meaningful allocation to high-quality equities — businesses with pricing power, low leverage, and strong free cash flow generation. These companies have historically performed well in the early stages of easing cycles, when the macro environment remains uncertain.
            </p>
            <p className="mb-4">
              Second, we are selectively adding duration. After the bond market rout of 2022–2023, long-dated investment-grade bonds offer yields that are attractive on both an absolute and a real basis. As the easing cycle progresses, we expect duration to be a source of both income and capital appreciation.
            </p>
            <p className="mb-4">
              Third, we are watching emerging markets closely. A weaker dollar, falling US rates, and improving global growth dynamics create a constructive backdrop for selective EM exposure. We are particularly focused on markets with strong domestic demand, improving fiscal positions, and undervalued currencies.
            </p>
            <p>
              The rate cycle is, ultimately, a tide that lifts and lowers all boats. But the boats that are best positioned — those with quality construction, appropriate ballast, and skilled navigation — will ride the tide most effectively. That is the investment philosophy that guides our approach, and it is one that history consistently validates.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Conclusion
            </h2>
            <p className="mb-4">
              Five decades of rate cycle history offer a clear message: the investors who outperform are not those who predict cycles with precision — no one can — but those who understand the structural dynamics of each phase and position their portfolios accordingly. They maintain quality through uncertainty, add duration when yields are attractive, and selectively increase risk exposure as the easing cycle matures.
            </p>
            <p>
              The current environment is one of the most interesting in a generation. The combination of moderating inflation, a resilient economy, and an easing central bank creates a backdrop that is, on balance, constructive for patient, disciplined investors. We remain focused on capital preservation first, and capital appreciation second — the order of priorities that has guided Macro Capital since inception.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-16 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>
            This article is for informational purposes only and does not constitute investment advice. Past performance is not indicative of future results. For qualified investors only.
          </p>
          <Link
            href="/#insights"
            className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
            style={{ color: 'var(--primary)' }}
          >
            <ArrowLeft size={14} />
            Back to all perspectives
          </Link>
        </div>
      </article>
    </div>
  );
}
